import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api-client';
import { BarChart3, TrendingUp, Users, Server, DollarSign, MessageSquare, RefreshCw } from 'lucide-react';

export default function AdminAnalytics() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const isSuperuser = user?.is_superuser || user?.is_admin || user?.is_staff;

  useEffect(() => {
    if (!isSuperuser) {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [isSuperuser, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [analyticsData, statsData] = await Promise.all([
        api.getAnalytics(),
        api.getAdminStats(),
      ]);
      setAnalytics(analyticsData);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isSuperuser) return null;

  const statCards = [
    { label: 'Total Users', value: stats?.users?.total || 0, subValue: `${stats?.users?.active || 0} active`, icon: Users, color: 'emerald' },
    { label: 'Total Instances', value: stats?.instances?.total || 0, subValue: `${stats?.instances?.running || 0} running`, icon: Server, color: 'cyan' },
    { label: 'Total Revenue', value: `$${analytics?.totalCost?.toFixed(2) || '0.00'}`, subValue: 'this month', icon: DollarSign, color: 'amber' },
    { label: 'Total Messages', value: analytics?.totalMessages?.toLocaleString() || 0, subValue: 'processed', icon: MessageSquare, color: 'purple' },
  ];

  return (
    <div data-testid="admin-analytics" className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BarChart3 className="text-purple-500" size={24} />
          <div>
            <h1 className="text-2xl font-bold text-white font-mono">Platform Analytics</h1>
            <p className="text-sm text-zinc-500">System-wide statistics and insights</p>
          </div>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-zinc-800 text-zinc-300 rounded-sm hover:bg-zinc-700 transition-all font-mono text-sm"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-5 animate-pulse">
              <div className="h-4 bg-zinc-800 rounded w-1/2 mb-3"></div>
              <div className="h-8 bg-zinc-800 rounded w-1/3 mb-1"></div>
              <div className="h-3 bg-zinc-800 rounded w-1/4"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {statCards.map((card, i) => (
            <div key={i} className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-mono text-zinc-500 uppercase tracking-wider">{card.label}</span>
                <card.icon size={18} className={`text-${card.color}-500`} />
              </div>
              <p className="text-2xl font-bold text-white font-mono">{card.value}</p>
              <p className="text-xs text-zinc-500 mt-1">{card.subValue}</p>
            </div>
          ))}
        </div>
      )}

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Messages Chart */}
        <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-5">
          <h3 className="text-white font-mono font-medium mb-4 flex items-center gap-2">
            <TrendingUp size={16} className="text-emerald-500" />
            Daily Messages (30 days)
          </h3>
          {loading ? (
            <div className="h-48 bg-zinc-800 rounded animate-pulse"></div>
          ) : analytics?.dailyMessages ? (
            <div className="h-48 flex items-end gap-1">
              {analytics.dailyMessages.slice(-30).map((day, i) => (
                <div
                  key={i}
                  className="flex-1 bg-emerald-500/20 hover:bg-emerald-500/40 transition-all rounded-t"
                  style={{ height: `${(day.messages / 800) * 100}%` }}
                  title={`${day.date}: ${day.messages} messages`}
                />
              ))}
            </div>
          ) : (
            <p className="text-zinc-500 text-sm">No data available</p>
          )}
        </div>

        {/* Cost by Instance */}
        <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-5">
          <h3 className="text-white font-mono font-medium mb-4 flex items-center gap-2">
            <DollarSign size={16} className="text-amber-500" />
            Cost by Instance
          </h3>
          {loading ? (
            <div className="space-y-3">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="h-8 bg-zinc-800 rounded animate-pulse"></div>
              ))}
            </div>
          ) : analytics?.costByInstance?.length > 0 ? (
            <div className="space-y-3">
              {analytics.costByInstance.map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <span className="text-sm font-mono text-zinc-300">{item.name}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-500 rounded-full"
                        style={{ width: `${(item.cost / 100) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-mono text-amber-400 w-16 text-right">${item.cost.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-500 text-sm">No data available</p>
          )}
        </div>
      </div>

      {/* Platform Summary */}
      <div className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-5">
        <h3 className="text-white font-mono font-medium mb-4">Platform Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs font-mono text-zinc-500 uppercase">Blog Posts</p>
            <p className="text-lg font-mono text-white">{stats?.blog?.total || 0} <span className="text-xs text-emerald-400">({stats?.blog?.published || 0} published)</span></p>
          </div>
          <div>
            <p className="text-xs font-mono text-zinc-500 uppercase">Skills</p>
            <p className="text-lg font-mono text-white">{stats?.skills?.total || 0}</p>
          </div>
          <div>
            <p className="text-xs font-mono text-zinc-500 uppercase">Avg Uptime</p>
            <p className="text-lg font-mono text-white">{analytics?.avgUptime || 99.7}%</p>
          </div>
          <div>
            <p className="text-xs font-mono text-zinc-500 uppercase">Active Instances</p>
            <p className="text-lg font-mono text-white">{analytics?.activeInstances || 0}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
